/**
 * Contact outreach actions — Call, AI Call, Message, Email — extracted
 * from Contact 360 so any page with a Contact can render them (the lead
 * detail panel and contacts directory are next). These are app-level
 * components, not ui-package primitives: they own their react-query
 * fetches (contact channels), mutations, and router navigation, and only
 * need the Contact itself from the caller.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Mail, MessageSquare, Phone, PhoneOff, Sparkles } from "lucide-react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  cn,
  toast,
} from "@lumika/ui";
import { type Contact } from "@lumika/shared";
import { bdcApi } from "../api/bdc";
import { telephonyApi } from "../api/telephony";
import { Softphone, type SoftphoneState } from "../lib/twilio-device";

// ---------------------------------------------------------------------------
// Outbound voice call
//
// Lets the operator dial the contact using the same Twilio Voice SDK
// softphone the Telephony settings page uses. Phone numbers come from
// the contact's `primary_phone` plus every `contact_channels` row of
// `type='phone'`. When there's exactly one destination we open the call
// dialog and auto-dial as soon as the softphone has registered; when
// there's more than one, the dialog shows a picker first so the
// operator chooses which line to ring.
//
// The button stays disabled with a tooltip when no phone number is
// attached at all — preventing a misclick that would invariably fail
// inside the dialer with "Enter a destination number".
// ---------------------------------------------------------------------------

export function collectContactPhones(
  contact: Contact,
  channels: Array<{ type: string; value: string | null }> | undefined,
): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  const add = (raw: string | null | undefined) => {
    if (!raw) return;
    const trimmed = raw.trim();
    if (!trimmed) return;
    if (seen.has(trimmed)) return;
    seen.add(trimmed);
    out.push(trimmed);
  };
  add(contact.primaryPhone);
  for (const ch of channels ?? []) {
    if (ch.type === "phone") add(ch.value);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Capability gating
//
// One dealership-level query each (shared react-query cache — cheap even
// on 25-row tables): which locations can place calls, and which ACTIVE
// channels exist per outbound kind. Buttons for capabilities the
// dealership simply doesn't have are HIDDEN, not disabled — a disabled
// "Call" on a dealership with no telephony reads as a bug, not a state.
// ---------------------------------------------------------------------------

/** Voice-capable locations for this user. Empty = hide Call buttons. */
export function useVoiceCapabilities(): {
  rooftops: Array<{ id: string; name: string }>;
  isLoading: boolean;
} {
  const q = useQuery({
    queryKey: ["telephony", "voice-capabilities"],
    queryFn: () => telephonyApi.voiceCapabilities(),
    staleTime: 5 * 60_000,
  });
  return { rooftops: q.data?.rooftops ?? [], isLoading: q.isLoading };
}

/** ACTIVE outbound channels of one kind (sms → `sms`, email → `email_alias`,
 * the only send-capable email type). Empty = hide that button. */
export function useActiveOutboundChannels(kind: "sms" | "email"): {
  channels: Array<{ id: string; name: string; rooftopId: string | null }>;
  isLoading: boolean;
} {
  const q = useQuery({
    queryKey: ["bdc", "channels"],
    queryFn: () => bdcApi.channels(),
    staleTime: 5 * 60_000,
  });
  const wanted = kind === "sms" ? "sms" : "email_alias";
  const channels = (q.data?.items ?? [])
    .filter((c) => c.type === wanted && c.status === "active")
    .map((c) => ({ id: c.id, name: c.name, rooftopId: c.rooftopId }));
  return { channels, isLoading: q.isLoading };
}

export function CallButton({
  contact,
  unavailableFallback = null,
  onBeforeOpen,
}: {
  contact: Contact;
  unavailableFallback?: ReactNode;
  onBeforeOpen?: () => void;
}) {
  const voice = useVoiceCapabilities();
  const channelsQuery = useQuery({
    queryKey: ["bdc", "contact-channels", contact.id],
    queryFn: () => bdcApi.contactChannels(contact.id),
  });
  const phones = useMemo(
    () => collectContactPhones(contact, channelsQuery.data?.items),
    [contact, channelsQuery.data],
  );
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState<string | null>(null);

  // No voice-capable location → there is nothing this button could ever
  // do here. Hidden, not disabled (and hidden while loading, so it
  // appears once real rather than flickering away).
  if (voice.isLoading) {
    return unavailableFallback ? (
      <Button size="sm" variant="outline" disabled>Checking calling…</Button>
    ) : null;
  }
  if (voice.rooftops.length === 0) return <>{unavailableFallback}</>;

  if (phones.length === 0) {
    return (
      <DisabledCallAction reason="No phone number on file — add one to call this contact." />
    );
  }

  // Voice opt-out disables dialing the same way SMS/email opt-outs
  // disable Message/Email — the consent flag must gate every surface
  // that can ring the customer, not just the message composers.
  if (contact.optoutVoice) {
    return (
      <DisabledCallAction reason="Contact has opted out of voice calls." />
    );
  }

  const onClick = () => {
    if (phones.length === 1) {
      setTarget(phones[0]!);
    } else {
      setTarget(null);
    }
    onBeforeOpen?.();
    setOpen(true);
  };

  return (
    <>
      <Button size="sm" variant="outline" onClick={onClick}>
        <Phone className="h-4 w-4" />
        Call
      </Button>
      <CallDialog
        open={open}
        onClose={() => {
          setOpen(false);
          setTarget(null);
        }}
        contact={contact}
        phones={phones}
        initialTarget={target}
        onPickTarget={setTarget}
        rooftops={voice.rooftops}
      />
    </>
  );
}

function DisabledCallAction({ reason }: { reason: string }): React.ReactElement {
  return (
    <span className="inline-flex max-w-56 flex-col items-start gap-1">
      <Button size="sm" variant="outline" disabled>
        <Phone className="h-4 w-4" />
        Call
      </Button>
      <span className="text-xs leading-4 text-muted-foreground">{reason}</span>
    </span>
  );
}

interface CallDialogProps {
  open: boolean;
  onClose: () => void;
  contact: Contact;
  phones: string[];
  initialTarget: string | null;
  onPickTarget: (n: string) => void;
  /** Voice-capable locations (from useVoiceCapabilities). One = used
   * silently; several = the dialog offers a "call from" picker. */
  rooftops: Array<{ id: string; name: string }>;
}

/**
 * Lifecycle: the dialog opens on a CONFIRMATION screen — the operator
 * sees (and can change) the number to dial and, when the dealership has
 * several voice-capable locations, which location to call from. Nothing
 * rings until they press "Call": only then is the Softphone started
 * (with the chosen location's token) and the dial placed on `ready`.
 *
 * The Softphone is torn down on dialog close OR call end — never
 * carried across sessions, since the access token is rooftop-scoped
 * and the next call may be for a different rooftop.
 */
export function CallDialog({
  open,
  onClose,
  contact,
  phones,
  initialTarget,
  onPickTarget,
  rooftops,
}: CallDialogProps) {
  const qc = useQueryClient();
  const [target, setTarget] = useState<string | null>(initialTarget);
  const [rooftopId, setRooftopId] = useState<string | null>(
    rooftops[0]?.id ?? null,
  );
  const [confirmed, setConfirmed] = useState(false);
  const [phoneState, setPhoneState] = useState<SoftphoneState>({ kind: "idle" });
  const phoneRef = useRef<Softphone | null>(null);
  const dialedFor = useRef<string | null>(null);

  useEffect(() => {
    setTarget(initialTarget);
  }, [initialTarget]);

  // Spin up + tear down the softphone with the CONFIRMATION, not the
  // dialog: no token is minted and nothing can ring while the operator
  // is still deciding.
  useEffect(() => {
    if (!open || !confirmed) {
      const ph = phoneRef.current;
      phoneRef.current = null;
      dialedFor.current = null;
      setPhoneState({ kind: "idle" });
      if (ph) void ph.destroy();
      return;
    }
    if (phoneRef.current) return;
    const phone = new Softphone({
      onState: setPhoneState,
      onError: (err) => toast.error(err.message),
    });
    phoneRef.current = phone;
    void (async () => {
      try {
        await phone.start(rooftopId ? { rooftopId } : {});
      } catch (err) {
        toast.error((err as Error).message);
        await phone.destroy();
        phoneRef.current = null;
        onClose();
      }
    })();
  }, [open, confirmed, rooftopId, onClose]);

  // Auto-dial once we have both a target AND a registered softphone.
  // `dialedFor` guards against a double-dial when state churns through
  // `ready` more than once (e.g. after a tokenWillExpire refresh).
  // We always prep the activity row server-side first, then pass its
  // id to Twilio as a custom param so the webhook can stamp the
  // CallSid back onto it and the inbox / Customer 360 timeline render
  // the call as an activity bubble.
  useEffect(() => {
    if (!open || !confirmed || !target) return;
    if (phoneState.kind !== "ready") return;
    if (dialedFor.current === target) return;
    const dest = target;
    dialedFor.current = dest;
    void (async () => {
      let activityId: string | undefined;
      let leadId: string | undefined;
      try {
        const prep = await telephonyApi.prepareOutboundCall({
          contactId: contact.id,
          to: dest,
          mode: "operator",
          ...(rooftopId ? { rooftopId } : {}),
        });
        activityId = prep.activityId;
        leadId = prep.leadId;
        // Surface the new activity + lead immediately in the
        // background — by the time the call lands the inbox / Customer
        // 360 timeline already shows the call bubble.
        await qc.invalidateQueries({
          queryKey: ["bdc", "contact-activities", contact.id],
        });
        await qc.invalidateQueries({
          queryKey: ["bdc", "contact-leads", contact.id],
        });
      } catch (err) {
        // Prep is best-effort: if it fails we still place the call
        // (the operator's primary need) — they just won't get the
        // activity bubble on the timeline for this one.
        toast.error(
          `Couldn't prep call activity: ${(err as Error).message}`,
        );
      }
      try {
        await phoneRef.current?.dial({
          to: dest,
          extraParams: {
            ...(activityId ? { ActivityId: activityId } : {}),
            ...(leadId ? { LeadId: leadId } : {}),
            ContactId: contact.id,
          },
        });
      } catch (err) {
        toast.error((err as Error).message);
      }
    })();
  }, [open, confirmed, target, rooftopId, phoneState, contact.id, qc]);

  const hangup = () => {
    phoneRef.current?.hangup();
  };

  const stateLabel = (() => {
    switch (phoneState.kind) {
      case "idle":
      case "registering":
        return "Connecting softphone…";
      case "ready":
        return target ? `Dialing ${target}…` : "Softphone ready";
      case "connecting":
        return `Dialing ${phoneState.to}…`;
      case "in_call":
        return `Connected to ${phoneState.to}`;
      case "ended":
        return "Call ended";
    }
  })();
  const inCall =
    phoneState.kind === "connecting" || phoneState.kind === "in_call";

  const close = () => {
    setConfirmed(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (o) return;
        // Closing tears down the softphone, which drops a live call —
        // hanging up must stay the explicit footer action, never a
        // stray Escape / X / overlay click.
        if (inCall) {
          toast.info("Hang up before closing the call window.");
          return;
        }
        close();
      }}
    >
      <DialogContent className="sm:max-w-sm" preventDismiss={inCall}>
        <DialogHeader>
          <DialogTitle>Call {contact.firstName ?? "contact"}</DialogTitle>
        </DialogHeader>
        {!confirmed ? (
          <>
            {phones.length > 1 ? (
              <>
                <p className="text-sm text-muted-foreground">
                  This contact has multiple phone numbers. Pick which one
                  to call:
                </p>
                <div className="grid gap-2">
                  {phones.map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => {
                        setTarget(n);
                        onPickTarget(n);
                      }}
                      className={cn(
                        "flex items-center justify-between gap-3 rounded-md border px-3 py-2 text-left text-sm hover:bg-muted",
                        target === n
                          ? "border-primary bg-primary/10"
                          : "border-border bg-card",
                      )}
                    >
                      <span className="flex items-center gap-2">
                        <Phone className="h-4 w-4" />
                        <span className="font-mono">{n}</span>
                      </span>
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">
                Call {contact.firstName ?? "this contact"} at{" "}
                <span className="font-mono">{target}</span>?
              </p>
            )}
            {rooftops.length > 1 ? (
              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground">
                  Call from location
                </p>
                <Select
                  value={rooftopId ?? undefined}
                  onValueChange={(v) => setRooftopId(v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Pick a location" />
                  </SelectTrigger>
                  <SelectContent>
                    {rooftops.map((r) => (
                      <SelectItem key={r.id} value={r.id}>
                        {r.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}
          </>
        ) : (
          <div className="flex flex-col items-center gap-3 py-4">
            <Phone className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm font-medium">{stateLabel}</p>
            {target ? (
              <p className="font-mono text-xs text-muted-foreground">{target}</p>
            ) : null}
          </div>
        )}
        <DialogFooter>
          {!confirmed ? (
            <>
              <Button variant="ghost" onClick={close}>
                Cancel
              </Button>
              <Button
                disabled={!target || (rooftops.length > 1 && !rooftopId)}
                onClick={() => setConfirmed(true)}
              >
                <Phone className="h-4 w-4" />
                Call
              </Button>
            </>
          ) : inCall ? (
            <Button variant="destructive" onClick={hangup}>
              <PhoneOff className="h-4 w-4" />
              Hang up
            </Button>
          ) : (
            <Button variant="outline" onClick={close}>
              Close
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// AI Call
//
// Initiates a server-side outbound call: Twilio dials the contact, and
// the agent's voice agent runs the lead through the existing
// LeadRelay-based voice-relay infrastructure. The operator
// doesn't open a softphone here — the call happens entirely on the
// backend. The Customer 360 timeline gets a `type='call'` activity
// with `actor_type='ai'` so the bubble renders correctly.
//
// Uses the same phone-picker logic as the regular Call button (no
// phones → disabled, one phone → dial, multiple → picker), since the
// destination question is identical.
// ---------------------------------------------------------------------------

export function AiCallButton({
  contact,
  onBeforeOpen,
}: {
  contact: Contact;
  onBeforeOpen?: () => void;
}) {
  const qc = useQueryClient();
  const voice = useVoiceCapabilities();
  const channelsQuery = useQuery({
    queryKey: ["bdc", "contact-channels", contact.id],
    queryFn: () => bdcApi.contactChannels(contact.id),
  });
  const phones = useMemo(
    () => collectContactPhones(contact, channelsQuery.data?.items),
    [contact, channelsQuery.data],
  );
  const [pickerOpen, setPickerOpen] = useState(false);
  const [selectedPhone, setSelectedPhone] = useState<string | null>(null);
  const [rooftopId, setRooftopId] = useState<string | null>(null);
  const [notes, setNotes] = useState("");
  const initiate = useMutation({
    mutationFn: async (to: string) => {
      const fromRooftop = rooftopId ?? voice.rooftops[0]?.id;
      return telephonyApi.prepareOutboundCall({
        contactId: contact.id,
        to,
        mode: "ai",
        ...(fromRooftop ? { rooftopId: fromRooftop } : {}),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      });
    },
    onSuccess: async () => {
      await qc.invalidateQueries({
        queryKey: ["bdc", "contact-activities", contact.id],
      });
      await qc.invalidateQueries({
        queryKey: ["bdc", "contact-leads", contact.id],
      });
      toast.success("Assistant call started");
    },
    onError: (err) => {
      toast.error(`Assistant call failed: ${(err as Error).message}`);
    },
  });

  // Same capability gate as the operator Call button: no voice-capable
  // location → no button at all.
  if (voice.isLoading || voice.rooftops.length === 0) return null;

  if (phones.length === 0) {
    return (
      <DisabledAssistantCallAction reason="No phone number on file — add one before the Assistant can call this contact." />
    );
  }

  // Same consent gate as the operator Call button — the AI agent must
  // never ring a customer who opted out of voice.
  if (contact.optoutVoice) {
    return (
      <DisabledAssistantCallAction reason="Contact has opted out of voice calls." />
    );
  }

  // Always open the dialog — even with a single number — so one
  // misclick can't ring the customer with the AI agent unconfirmed
  // (the server dials immediately; there's no recall once placed).
  const onClick = () => {
    onBeforeOpen?.();
    setPickerOpen(true);
  };

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        onClick={onClick}
        disabled={initiate.isPending}
      >
        <Sparkles className="h-4 w-4" />
        {initiate.isPending ? "Calling…" : "Have Assistant call"}
      </Button>
      <Dialog
        open={pickerOpen}
        onOpenChange={(o) => {
          setPickerOpen(o);
          // A stale selection must never survive into the next open —
          // the server dials immediately with no recall, so every
          // session re-confirms the number from scratch.
          if (!o) {
            setSelectedPhone(null);
            setNotes("");
          }
        }}
      >
        <DialogContent
          className="sm:max-w-sm"
          preventDismiss={notes.trim().length > 0 || initiate.isPending}
        >
          <DialogHeader>
            <DialogTitle>Have Assistant call {contact.firstName ?? "contact"}</DialogTitle>
          </DialogHeader>
          {phones.length === 1 ? (
            <p className="text-sm text-muted-foreground">
              The Assistant will call {contact.firstName ?? "this contact"} at{" "}
              <span className="font-mono">{phones[0]}</span>.
            </p>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">
                This contact has multiple phone numbers. Pick which one the
                Assistant should call:
              </p>
              <div className="grid gap-2">
                {phones.map((n) => (
                  <button
                    key={n}
                    type="button"
                    disabled={initiate.isPending}
                    onClick={() => setSelectedPhone(n)}
                    className={cn(
                      "flex items-center justify-between gap-3 rounded-md border px-3 py-2 text-left text-sm hover:bg-muted",
                      selectedPhone === n
                        ? "border-primary bg-primary/10"
                        : "border-border bg-card",
                    )}
                  >
                    <span className="flex items-center gap-2">
                      <Phone className="h-4 w-4" />
                      <span className="font-mono">{n}</span>
                    </span>
                  </button>
                ))}
              </div>
            </>
          )}
          {voice.rooftops.length > 1 ? (
            <div className="space-y-1">
              <p className="text-xs font-medium text-muted-foreground">
                Call from location
              </p>
              <Select
                value={rooftopId ?? voice.rooftops[0]!.id}
                onValueChange={(v) => setRooftopId(v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Pick a location" />
                </SelectTrigger>
                <SelectContent>
                  {voice.rooftops.map((r) => (
                    <SelectItem key={r.id} value={r.id}>
                      {r.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}
          <div className="space-y-1">
            <p className="text-xs font-medium text-muted-foreground">
              Notes for the Assistant (optional)
            </p>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Confirm 2pm Thursday test drive appointment"
              maxLength={500}
              rows={2}
            />
            <p className="text-xs text-muted-foreground text-right">
              {notes.length}/500
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => {
                setPickerOpen(false);
                setSelectedPhone(null);
                setNotes("");
              }}
            >
              Cancel
            </Button>
            {/* ALWAYS an explicit confirm — picking a number must never
                ring the customer by itself. */}
            <Button
              disabled={
                initiate.isPending ||
                (phones.length > 1 && !selectedPhone)
              }
              onClick={() => {
                const to = phones.length === 1 ? phones[0]! : selectedPhone!;
                setPickerOpen(false);
                setSelectedPhone(null);
                setNotes("");
                initiate.mutate(to);
              }}
            >
              <Sparkles className="h-4 w-4" />
              Start Assistant call
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function DisabledAssistantCallAction({ reason }: { reason: string }): React.ReactElement {
  return (
    <span className="inline-flex max-w-64 flex-col items-start gap-1">
      <Button size="sm" variant="outline" disabled>
        <Sparkles className="h-4 w-4" />
        Assistant call
      </Button>
      <span className="text-xs leading-4 text-muted-foreground">{reason}</span>
    </span>
  );
}

// ---------------------------------------------------------------------------
// Message / Email
//
// Start a new SMS/email conversation from the contact header. The
// server resolves the channel and recipient identity itself; here we
// only need to know an identity exists. Like the Call button, that
// means primary_phone/primary_email OR a matching `contact_channels`
// row — keying off the primaries alone showed a dead Message button
// for contacts whose only phone lives in contact_channels (the backend
// start-conversation flow gained the same fallback this wave).
// ---------------------------------------------------------------------------

export function MessageButton({ contact }: { contact: Contact }) {
  const outbound = useActiveOutboundChannels("sms");
  const channelsQuery = useQuery({
    queryKey: ["bdc", "contact-channels", contact.id],
    queryFn: () => bdcApi.contactChannels(contact.id),
  });
  const channelPhone =
    (channelsQuery.data?.items ?? []).find(
      (ch) => ch.type === "phone" && ch.value,
    )?.value ?? null;
  // No active SMS channel anywhere in the dealership → nothing this
  // button could send through. Hidden, not disabled.
  if (outbound.isLoading || outbound.channels.length === 0) return null;
  return (
    <StartConversationButton
      contact={contact}
      channel="sms"
      label="Message"
      icon={<MessageSquare className="h-4 w-4" />}
      target={contact.primaryPhone ?? channelPhone}
      optedOut={contact.optoutSms}
      disabledNoTarget="No phone number on file"
      disabledOptedOut="Contact has opted out of SMS"
      placeholder="Type the first text message…"
      sendChannels={outbound.channels}
    />
  );
}

export function EmailButton({ contact }: { contact: Contact }) {
  const outbound = useActiveOutboundChannels("email");
  const channelsQuery = useQuery({
    queryKey: ["bdc", "contact-channels", contact.id],
    queryFn: () => bdcApi.contactChannels(contact.id),
  });
  const channelEmail =
    (channelsQuery.data?.items ?? []).find(
      (ch) => ch.type === "email" && ch.value,
    )?.value ?? null;
  // No active sendable email channel (email_alias) → hide the button.
  if (outbound.isLoading || outbound.channels.length === 0) return null;
  return (
    <StartConversationButton
      contact={contact}
      channel="email"
      label="Email"
      icon={<Mail className="h-4 w-4" />}
      target={contact.primaryEmail ?? channelEmail}
      optedOut={contact.optoutEmail}
      disabledNoTarget="No email address on file"
      disabledOptedOut="Contact has opted out of email"
      placeholder="Write your email…"
      sendChannels={outbound.channels}
    />
  );
}

/**
 * "Message" / "Email" action: opens a compose dialog, then atomically starts
 * a new SMS/email conversation on a real channel (resolving the channel,
 * ensuring the contact's channel identity, creating/reusing the lead, and
 * sending the first message) and opens the thread in the inbox. Disabled with
 * a reason when the contact has no phone/email or has opted out.
 */
export function StartConversationButton({
  contact,
  channel,
  label,
  icon,
  target,
  optedOut,
  disabledNoTarget,
  disabledOptedOut,
  placeholder,
  sendChannels = [],
}: {
  contact: Contact;
  channel: "sms" | "email";
  label: string;
  icon: ReactNode;
  target: string | null;
  optedOut: boolean;
  disabledNoTarget: string;
  disabledOptedOut: string;
  placeholder: string;
  /** Active channels of this kind. More than one → the dialog offers a
   * "send via" picker; otherwise the server resolves the channel. */
  sendChannels?: Array<{ id: string; name: string }>;
}) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [channelId, setChannelId] = useState<string | null>(null);

  const send = useMutation({
    mutationFn: () => {
      const trimmedSubject = subject.trim();
      const picked = channelId ?? sendChannels[0]?.id;
      return bdcApi.startConversation(contact.id, {
        channel,
        body: body.trim(),
        // Explicit channel choice only matters when there IS a choice —
        // otherwise let the server resolve (it also covers callers that
        // didn't pass sendChannels).
        ...(sendChannels.length > 1 && picked ? { channelId: picked } : {}),
        // Subject is email-only and optional — omit the key entirely when
        // blank so the DTO's min(1) doesn't reject an empty string.
        ...(channel === "email" && trimmedSubject
          ? { subject: trimmedSubject }
          : {}),
      });
    },
    onSuccess: async (lead) => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["bdc", "contact-leads", contact.id] }),
        qc.invalidateQueries({ queryKey: ["bdc", "leads"] }),
        qc.invalidateQueries({
          queryKey: ["bdc", "contact-activities", contact.id],
        }),
      ]);
      setOpen(false);
      setSubject("");
      setBody("");
      navigate(`/inbox/${lead.id}`);
    },
    onError: (err) =>
      toast.error((err as Error).message ?? "Could not send the message."),
  });

  const disabledReason = !target
    ? disabledNoTarget
    : optedOut
      ? disabledOptedOut
      : null;

  // Icon-only call sites (compact table rows) pass label="" — the dialog
  // title must still carry the action verb.
  const action = label || (channel === "sms" ? "Message" : "Email");

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        disabled={Boolean(disabledReason)}
        title={disabledReason ?? undefined}
        // Icon-only call sites (compact table rows) pass label="" — the
        // accessible name must survive that.
        aria-label={label || (channel === "sms" ? "Send SMS" : "Send email")}
        onClick={() => setOpen(true)}
      >
        {icon}
        {label}
      </Button>
      <Dialog
        open={open}
        onOpenChange={(o) => {
          setOpen(o);
          if (!o) {
            setSubject("");
            setBody("");
          }
        }}
      >
        <DialogContent
          className="sm:max-w-md"
          preventDismiss={
            send.isPending || body.trim().length > 0 || subject.trim().length > 0
          }
        >
          <DialogHeader>
            <DialogTitle>
              {action} {contact.firstName ?? "contact"}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {channel === "sms" ? `Sends an SMS to ${target}.` : `Emails ${target}.`}{" "}
            The conversation opens in your inbox.
          </p>
          {sendChannels.length > 1 ? (
            <div className="space-y-1">
              <p className="text-xs font-medium text-muted-foreground">
                Send via
              </p>
              <Select
                value={channelId ?? sendChannels[0]!.id}
                onValueChange={(v) => setChannelId(v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Pick a channel" />
                </SelectTrigger>
                <SelectContent>
                  {sendChannels.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}
          {channel === "email" ? (
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Subject (optional)"
              maxLength={200}
            />
          ) : null}
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={placeholder}
            rows={5}
            autoFocus
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => send.mutate()}
              disabled={send.isPending || body.trim().length === 0}
            >
              {send.isPending ? "Sending…" : "Send"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
